import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface Notification {
  id: string;
  type: 'request' | 'score' | 'approval' | 'validation_request' | 'sao_feedback' | 'nudge';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  data?: any;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  initialized: boolean;
  initialize: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotifications: () => void;
  setupRealtimeSubscriptions: () => Promise<void>;
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  initialized: false,

  initialize: async () => {
    try {
      console.log('[Notifications] initialize called');
      set({ loading: true });
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('[Notifications] No user found during initialize');
        return;
      }
      console.log('[Notifications] Current user ID:', user.id);
      // Fetch existing notifications
      const { data: notifications, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('[Notifications] Error fetching notifications:', error);
        throw error;
      }
      console.log('[Notifications] Notifications fetched:', notifications);
      set({
        notifications: notifications || [],
        unreadCount: notifications?.filter(n => !n.read).length || 0,
        loading: false,
        initialized: true
      });
      // Setup real-time subscriptions
      await get().setupRealtimeSubscriptions();
    } catch (error) {
      console.error('Error initializing notifications:', error);
      set({ loading: false, initialized: false });
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: state.unreadCount - 1
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      set(state => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  },

  clearNotifications: () => {
    set({ notifications: [], unreadCount: 0 });
  },

  setupRealtimeSubscriptions: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Subscribe to new notifications
    const notificationsSubscription = supabase
      .channel('notifications-changes')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        }, 
        (payload) => {
          const notification = payload.new as Notification;
          set(state => ({
            notifications: [notification, ...state.notifications],
            unreadCount: !notification.read ? state.unreadCount + 1 : state.unreadCount
          }));
        }
      )
      .subscribe();

    // Add cleanup to window unload
    window.addEventListener('unload', () => {
      notificationsSubscription.unsubscribe();
    });
  }
})); 